import { Box, Typography, useMediaQuery } from '@mui/material';

import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useHauserEQResults } from '@/hooks/hauser-eq';

/** SVG icon paths matching the prototype exactly */
const ICON_PATHS: Record<string, Array<[string, boolean]>> = {
  bed: [
    ['M3 18V12C3 10.9 3.9 10 5 10H19C20.1 10 21 10.9 21 12V18', false],
    ['M3 18H21', false],
    ['M6 10V7C6 5.9 6.9 5 8 5H10C11.1 5 12 5.9 12 7V10', false],
  ],
  bath: [
    ['M4 12H20V16C20 18.2 18.2 20 16 20H8C5.8 20 4 18.2 4 16V12Z', false],
    ['M7 12V4C7 3.4 7.4 3 8 3H9C9.6 3 10 3.4 10 4V12', false],
    ['M6 20V22', false],
    ['M18 20V22', false],
  ],
  built: [
    ['M3 12L12 4L21 12', false],
    ['M5 10V20H19V10', false],
    ['M9 20V14H15V20', false],
  ],
  sqft: [
    ['M3 3V21', false],
    ['M3 21H21', false],
    ['M3 3L8 3', false],
    ['M3 8L6 8', false],
    ['M3 13L6 13', false],
    ['M8 21V18', false],
    ['M13 21V18', false],
    ['M18 21V18', false],
  ],
  lot: [
    ['M12 3C8.7 3 6 5.7 6 9C6 12.3 12 20 12 20C12 20 18 12.3 18 9C18 5.7 15.3 3 12 3Z', false],
    [
      'M12 7C10.9 7 10 7.9 10 9C10 10.1 10.9 11 12 11C13.1 11 14 10.1 14 9C14 7.9 13.1 7 12 7Z',
      false,
    ],
  ],
};

const StatIcon = ({ paths, size = 18 }: { paths: Array<[string, boolean]>; size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0, color: 'var(--tx-muted)' }}>
    {paths.map(([d, filled], j) => (
      <path key={j} d={d} fill={filled ? 'currentColor' : 'none'} />
    ))}
  </svg>
);

// The header's two ends mirror each other: address over city on the left, price over its label on the right, each a
// 16/400 line (Tradehaus's header weight, lighter than the hero figures below it) over a 12/500 muted one
const HEAD_LINE = { fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em', lineHeight: '20px', color: 'var(--tx-text)' } as const;
const SUB_LINE = { fontSize: 12, fontWeight: 500, lineHeight: '16px', color: 'var(--tx-muted)' } as const;

const PropertyHeader = () => {
  const { selectedProperty } = usePropertiesContext();
  const propertyId = selectedProperty?.property.id;
  const { propertyDetails, isCompleted: isHauserEQCompleted } = useHauserEQResults(propertyId);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const property = selectedProperty?.property;
  const details = isHauserEQCompleted && propertyDetails ? propertyDetails : null;

  const address = property?.address ?? '';
  const city = property?.city;
  const state = property?.state;
  const zipCode = property?.zipCode;
  const bedrooms = details?.bedrooms ?? property?.beds;
  const bathrooms = details?.bathrooms ?? property?.baths;
  const yearBuilt = details?.year_built ?? property?.yearBuilt;
  const sqft = details?.sqft ?? property?.sqft;
  const price = details?.price ?? property?.price;

  const lotSizeRaw = details?.lot_size ?? property?.lotSize;
  const lotAcres = lotSizeRaw
    ? typeof lotSizeRaw === 'number'
      ? Number((lotSizeRaw / 43560).toFixed(2))
      : lotSizeRaw
    : undefined;

  const locationLine = [city, state, zipCode].filter(Boolean).join(', ');

  const stats: Array<{ icon: string; value: string | number; label: string }> = [
    ...(bedrooms != null && bathrooms != null
      ? [
          { icon: 'bed', value: bedrooms, label: 'Bed' },
          { icon: 'bath', value: bathrooms, label: 'Bath' },
        ]
      : [
          ...(bedrooms != null ? [{ icon: 'bed', value: bedrooms, label: 'Bed' }] : []),
          ...(bathrooms != null ? [{ icon: 'bath', value: bathrooms, label: 'Bath' }] : []),
        ]),
    ...(yearBuilt != null ? [{ icon: 'built', value: yearBuilt, label: 'Built' }] : []),
    ...(sqft != null ? [{ icon: 'sqft', value: sqft.toLocaleString(), label: 'sq ft' }] : []),
    ...(lotAcres != null ? [{ icon: 'lot', value: lotAcres, label: 'acres' }] : []),
  ];

  // Mobile layout: 3-column dynamic property card (no address)
  if (isMobile) {
    return (
      <Box
        data-testid="property-header"
        sx={{
          background: 'var(--tx-glass-panel)',
          backgroundImage: 'var(--tx-surface-light)',
          boxShadow: 'var(--tx-shadow)',
          borderRadius: '0 0 25px 25px',
          padding: '14px 20px',
          margin: '0 10px 10px',
        }}>
        {/* 3-column grid: stats left, stats center, price right */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '6px 16px',
            fontSize: 13,
            color: 'var(--tx-faint)',
          }}>
          {/* Col 1 Row 1: Bed/Bath */}
          <div style={{ gridColumn: 1, gridRow: 1, display: 'flex', alignItems: 'center', gap: 5 }}>
            {bedrooms != null && bathrooms != null ? (
              <>
                <StatIcon paths={ICON_PATHS.bed} />
                <span style={{ color: 'var(--tx-text)', fontWeight: 600 }}>{bedrooms}</span>Bed
                <span style={{ color: '#999999' }}>/</span>
                <StatIcon paths={ICON_PATHS.bath} />
                <span style={{ color: 'var(--tx-text)', fontWeight: 600 }}>{bathrooms}</span>Bath
              </>
            ) : bedrooms != null ? (
              <>
                <StatIcon paths={ICON_PATHS.bed} />
                <span style={{ color: 'var(--tx-text)', fontWeight: 600 }}>{bedrooms}</span> Bed
              </>
            ) : bathrooms != null ? (
              <>
                <StatIcon paths={ICON_PATHS.bath} />
                <span style={{ color: 'var(--tx-text)', fontWeight: 600 }}>{bathrooms}</span> Bath
              </>
            ) : null}
          </div>
          {/* Col 2 Row 1: Year Built */}
          <div
            style={{
              gridColumn: 2,
              gridRow: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              paddingLeft: 40,
            }}>
            {yearBuilt != null && (
              <>
                <StatIcon paths={ICON_PATHS.built} />
                <span style={{ color: 'var(--tx-text)', fontWeight: 600 }}>{yearBuilt}</span> Built
              </>
            )}
          </div>
          {/* Col 3: List Price (spans 2 rows) */}
          <div
            style={{
              gridColumn: 3,
              gridRow: '1 / 3',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
            }}>
            {price != null && (
              <>
                <div style={{ fontSize: 11, color: '#999999' }}>List price</div>
                <div style={{ fontSize: 17, fontWeight: 600, color: '#ffffff' }}>
                  ${price.toLocaleString()}
                </div>
              </>
            )}
          </div>
          {/* Col 1 Row 2: Sq Ft */}
          <div style={{ gridColumn: 1, gridRow: 2, display: 'flex', alignItems: 'center', gap: 5 }}>
            {sqft != null && (
              <>
                <StatIcon paths={ICON_PATHS.sqft} />
                <span style={{ color: 'var(--tx-text)', fontWeight: 600 }}>
                  {sqft.toLocaleString()}
                </span>{' '}
                sq ft
              </>
            )}
          </div>
          {/* Col 2 Row 2: Lot Size */}
          <div
            style={{
              gridColumn: 2,
              gridRow: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              paddingLeft: 40,
            }}>
            {lotAcres != null && (
              <>
                <StatIcon paths={ICON_PATHS.lot} />
                <span style={{ color: 'var(--tx-text)', fontWeight: 600 }}>{lotAcres}</span> acres
              </>
            )}
          </div>
        </Box>
      </Box>
    );
  }

  // Desktop layout: one compact sheet — address over its city on the left, the facts with their icons beside it, the list
  // price as the single light hero figure on the right
  return (
    <Box
      data-testid="property-header"
      style={{
        background: 'var(--tx-glass-panel)',
        backgroundImage: 'var(--tx-surface-light)',
        boxShadow: 'var(--tx-shadow)',
        borderRadius: '24px',
        padding: '12px 20px',
        minHeight: 60,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '8px 24px',
        margin: '20px 0',
      }}>
      {/* Left: address + stats */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px 32px', flexWrap: 'wrap', minWidth: 0 }}>
        <div style={{ minWidth: 0 }}>
          <Typography data-testid="property-header-address" style={HEAD_LINE}>
            {address}
          </Typography>
          {locationLine && <Typography style={SUB_LINE}>{locationLine}</Typography>}
        </div>
        <div style={{ display: 'flex', gap: '6px 18px', flexWrap: 'wrap' }}>
          {stats.map((stat, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--tx-muted)',
                whiteSpace: 'nowrap',
              }}>
              <StatIcon paths={ICON_PATHS[stat.icon]} size={16} />
              <span style={{ color: 'var(--tx-text)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {stat.value}
              </span>
              {stat.label}
            </div>
          ))}
        </div>
      </div>

      {/* Right: list price, the address's mirror: same two lines, same type */}
      {price != null && (
        <div style={{ textAlign: 'right', flexShrink: 0 }} data-testid="property-header-price">
          <div style={{ ...HEAD_LINE, fontVariantNumeric: 'tabular-nums' }}>${price.toLocaleString()}</div>
          <div style={SUB_LINE}>List price</div>
        </div>
      )}
    </Box>
  );
};

export default PropertyHeader;
